import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

interface SurveyQuestion {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: 'text' | 'rating' | 'multiple_choice';
  options: string[] | null;
  sequence_order: number;
  is_required: boolean;
}

interface Survey {
  id: string;
  course_id: string;
  milestone: number | null;
  module_id: string | null;
  title: string;
  description: string | null;
  is_active: boolean;
  questions: SurveyQuestion[];
}

interface SurveyAnswer {
  question_id: string;
  answer_text?: string;
  answer_rating?: number;
  answer_choice?: string;
}

export function useCourseSurveys(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['course-surveys', courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('course_surveys')
        .select(`
          *,
          questions:survey_questions(*)
        `)
        .eq('course_id', courseId)
        .eq('is_active', true)
        .order('milestone', { nullsFirst: false });

      if (error) throw error;
      return data as Survey[];
    },
    enabled: !!courseId && !!user,
  });
}

export function useAllCourseSurveys(courseId: string) {
  return useQuery({
    queryKey: ['all-course-surveys', courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('course_surveys')
        .select(`
          *,
          questions:survey_questions(*),
          module:modules(id, title)
        `)
        .eq('course_id', courseId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!courseId,
  });
}

export function useCompletedSurveys(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['completed-surveys', courseId, user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('survey_responses')
        .select(`
          survey_id,
          survey:course_surveys!inner(course_id, milestone, module_id)
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      
      return data
        .filter((r: any) => r.survey?.course_id === courseId)
        .map((r: any) => r.survey_id);
    },
    enabled: !!courseId && !!user,
  });
}


export function usePendingSurvey(courseId: string, progress: number, currentModuleId?: string | null) {
  const { data: surveys } = useCourseSurveys(courseId);
  const { data: completedSurveys } = useCompletedSurveys(courseId);

  if (!surveys || !completedSurveys) return null;

  // First check for module-specific survey
  if (currentModuleId) {
    const moduleSurvey = surveys.find(
      s => s.module_id === currentModuleId && !completedSurveys.includes(s.id)
    );
    if (moduleSurvey) return moduleSurvey;
  }

  // Then check milestone surveys
  const milestones = [50, 75, 100];
  for (const milestone of milestones) {
    if (progress >= milestone) {
      const survey = surveys.find(s => s.milestone === milestone && !s.module_id);
      if (survey && !completedSurveys.includes(survey.id)) {
        return survey;
      }
    }
  }

  return null;
}

export function useSubmitSurvey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ surveyId, answers }: { surveyId: string; answers: SurveyAnswer[] }) => {
      if (!user) throw new Error('Not authenticated');

      const { data: response, error: responseError } = await supabase
        .from('survey_responses')
        .insert({ survey_id: surveyId, user_id: user.id })
        .select()
        .single();

      if (responseError) throw responseError;

      const answersToInsert = answers.map(answer => ({
        response_id: response.id,
        question_id: answer.question_id,
        answer_text: answer.answer_text || null,
        answer_rating: answer.answer_rating || null,
        answer_choice: answer.answer_choice || null,
      }));

      const { error: answersError } = await supabase
        .from('survey_answers')
        .insert(answersToInsert);

      if (answersError) throw answersError;

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['completed-surveys'] });
      toast.success('Thank you for your feedback!');
    },
    onError: (error) => {
      toast.error('Failed to submit survey');
      console.error(error);
    },
  });
}

export function useCreateSurvey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (survey: {
      course_id: string;
      title: string;
      description?: string;
      milestone?: number | null;
      module_id?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('course_surveys')
        .insert({
          course_id: survey.course_id,
          title: survey.title,
          description: survey.description || null,
          milestone: survey.milestone || null,
          module_id: survey.module_id || null,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['all-course-surveys', variables.course_id] });
      queryClient.invalidateQueries({ queryKey: ['course-surveys', variables.course_id] });
      toast.success('Survey created!');
    },
    onError: () => {
      toast.error('Failed to create survey');
    },
  });
}

export function useUpdateSurvey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      title?: string;
      description?: string | null;
      is_active?: boolean;
      milestone?: number | null;
      module_id?: string | null;
    }) => {
      const { error } = await supabase
        .from('course_surveys')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-course-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['course-surveys'] });
      toast.success('Survey updated!');
    },
    onError: () => {
      toast.error('Failed to update survey');
    },
  });
}

export function useDeleteSurvey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (surveyId: string) => {
      const { error } = await supabase
        .from('course_surveys')
        .delete()
        .eq('id', surveyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-course-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['course-surveys'] });
      toast.success('Survey deleted!');
    },
    onError: () => {
      toast.error('Failed to delete survey');
    },
  });
}

export function useAddSurveyQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (question: {
      survey_id: string;
      question_text: string;
      question_type: 'text' | 'rating' | 'multiple_choice';
      options?: string[];
      is_required?: boolean;
      sequence_order?: number;
    }) => {
      const { data, error } = await supabase
        .from('survey_questions')
        .insert({
          survey_id: question.survey_id,
          question_text: question.question_text,
          question_type: question.question_type,
          options: question.options || null,
          is_required: question.is_required ?? true,
          sequence_order: question.sequence_order ?? 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-course-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['course-surveys'] });
      toast.success('Question added!');
    },
    onError: () => {
      toast.error('Failed to add question');
    },
  });
}

export function useDeleteSurveyQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (questionId: string) => {
      const { error } = await supabase
        .from('survey_questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-course-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['course-surveys'] });
      toast.success('Question deleted!');
    },
    onError: () => {
      toast.error('Failed to delete question');
    },
  });
}

export function useSurveyResponseHistory(surveyId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['survey-response-history', surveyId, user?.id],
    enabled: !!surveyId && !!user,
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('survey_responses')
        .select(`
          id,
          completed_at,
          answers:survey_answers(question_id, answer_text, answer_rating, answer_choice)
        `)
        .eq('survey_id', surveyId)
        .eq('user_id', user.id)
        .order('completed_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

